import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  approvePaymentRequest,
  getApprovalsQueue,
  requestPaymentApproval,
  type ApprovalQueueItem,
  type CreateApprovalRequest,
} from '../lib/api';
import { useToast } from './Toast';

/**
 * Per-payment approvals queue (maker-checker). This surfaces and collects the approval; it
 * holds NO authority. The force-settle executor independently re-enforces that a matching
 * Approved entry exists (amount/currency/beneficiary) before it moves funds, and consumes it
 * single-use. Gating the ACTION, not development.
 */
export function ApprovalsQueue({ defaultKoreId }: { defaultKoreId?: string }): JSX.Element {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [koreId, setKoreId] = useState(defaultKoreId ?? '');
  const [uetr, setUetr] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('');
  const [beneficiary, setBeneficiary] = useState('');
  const [approvers, setApprovers] = useState('');
  const [requiredCount, setRequiredCount] = useState(2);

  const queue = useQuery({
    queryKey: ['approvals-queue'],
    queryFn: getApprovalsQueue,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const createMutation = useMutation<ApprovalQueueItem, Error, CreateApprovalRequest>({
    mutationFn: requestPaymentApproval,
    onSuccess: () => {
      toast.push('success', 'Approval requested — routed to the checker queue.');
      void queryClient.invalidateQueries({ queryKey: ['approvals-queue'] });
      setUetr('');
      setAmount('');
      setBeneficiary('');
    },
    onError: (e) => toast.push('error', `Could not request approval: ${e.message}`),
  });

  const approveMutation = useMutation<ApprovalQueueItem, Error, string>({
    mutationFn: (requestId: string) => approvePaymentRequest(requestId, 'Approved in console'),
    onSuccess: () => {
      toast.push('success', 'Vote recorded.');
      void queryClient.invalidateQueries({ queryKey: ['approvals-queue'] });
    },
    onError: (e) => toast.push('error', `Approve failed: ${e.message}`),
  });

  const submitRequest = (): void => {
    const parsedAmount = Number(amount);
    const approverList = approvers
      .split(',')
      .map((a) => a.trim())
      .filter((a) => a.length > 0);

    if (!koreId.trim() || !currency.trim() || !beneficiary.trim()) {
      toast.push('warn', 'KoreID, currency, and beneficiary are required.');
      return;
    }
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      toast.push('warn', 'Amount must be a positive number.');
      return;
    }
    if (approverList.length === 0) {
      toast.push('warn', 'At least one approver identity is required.');
      return;
    }
    if (requiredCount < 1 || requiredCount > approverList.length) {
      toast.push('warn', 'Required approvals must be between 1 and the number of approvers.');
      return;
    }

    createMutation.mutate({
      paymentId: koreId.trim(),
      uetr: uetr.trim() || undefined,
      amount: parsedAmount,
      currency: currency.trim().toUpperCase(),
      beneficiaryAccount: beneficiary.trim(),
      requiredApprovers: approverList,
      requiredApprovalCount: requiredCount,
    });
  };

  const items = queue.data ?? [];

  return (
    <div className="rounded-lg border border-slate-700 bg-kore-card p-5">
      <h2 className="mb-1 text-base font-semibold text-white">Payment Approvals Queue</h2>
      <p className="mb-4 text-xs text-slate-400">
        Every force-settle requires an approved entry here. Approvals are matched to the payment
        amount, currency, and beneficiary and are single-use.
      </p>

      <div className="mb-5 grid gap-2 rounded-md border border-slate-700 bg-slate-900/40 p-3 sm:grid-cols-2">
        <label className="text-xs text-slate-300">
          KoreID
          <input
            className="mt-1 w-full rounded border border-slate-600 bg-slate-800 px-2 py-1 text-sm text-white"
            value={koreId}
            onChange={(e) => setKoreId(e.target.value)}
            placeholder="settlement KoreID"
          />
        </label>
        <label className="text-xs text-slate-300">
          UETR (optional)
          <input
            className="mt-1 w-full rounded border border-slate-600 bg-slate-800 px-2 py-1 text-sm text-white"
            value={uetr}
            onChange={(e) => setUetr(e.target.value)}
          />
        </label>
        <label className="text-xs text-slate-300">
          Amount
          <input
            className="mt-1 w-full rounded border border-slate-600 bg-slate-800 px-2 py-1 text-sm text-white"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            placeholder="0.00"
          />
        </label>
        <label className="text-xs text-slate-300">
          Currency
          <input
            className="mt-1 w-full rounded border border-slate-600 bg-slate-800 px-2 py-1 text-sm uppercase text-white"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            maxLength={3}
            placeholder="USD"
          />
        </label>
        <label className="text-xs text-slate-300 sm:col-span-2">
          Beneficiary account
          <input
            className="mt-1 w-full rounded border border-slate-600 bg-slate-800 px-2 py-1 text-sm text-white"
            value={beneficiary}
            onChange={(e) => setBeneficiary(e.target.value)}
          />
        </label>
        <label className="text-xs text-slate-300 sm:col-span-2">
          Approvers (comma-separated identities)
          <input
            className="mt-1 w-full rounded border border-slate-600 bg-slate-800 px-2 py-1 text-sm text-white"
            value={approvers}
            onChange={(e) => setApprovers(e.target.value)}
            placeholder="checker1@korenet.cloud, checker2@korenet.cloud"
          />
        </label>
        <label className="text-xs text-slate-300">
          Required approvals
          <input
            type="number"
            min={1}
            className="mt-1 w-full rounded border border-slate-600 bg-slate-800 px-2 py-1 text-sm text-white"
            value={requiredCount}
            onChange={(e) => setRequiredCount(Number(e.target.value))}
          />
        </label>
        <div className="flex items-end">
          <button
            type="button"
            onClick={submitRequest}
            disabled={createMutation.isPending}
            className="w-full rounded bg-kore-accent px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {createMutation.isPending ? 'Requesting…' : 'Request approval'}
          </button>
        </div>
      </div>

      {queue.isLoading && <p className="text-sm text-slate-400">Loading queue…</p>}
      {queue.isError && <p className="text-sm text-rose-400">Approvals queue unavailable.</p>}
      {!queue.isLoading && !queue.isError && items.length === 0 && (
        <p className="text-sm text-slate-400">No approvals in the queue.</p>
      )}

      <ul className="space-y-2">
        {items.map((item) => {
          const approved = item.status === 'APPROVED_FOR_RELEASE';
          return (
            <li
              key={item.requestId}
              className="rounded-md border border-slate-700 bg-slate-900/40 p-3 text-sm text-slate-200"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium text-white">
                    {item.currency} {item.amount}
                  </div>
                  <div className="text-xs text-slate-400">
                    {item.beneficiary ?? item.beneficiaryAccount ?? '—'} · req {item.requestId.slice(0, 8)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`rounded px-2 py-0.5 text-xs font-semibold ${
                      approved ? 'bg-emerald-900/60 text-emerald-300' : 'bg-amber-900/60 text-amber-300'
                    }`}
                  >
                    {item.status}
                  </span>
                  {!approved && (
                    <button
                      type="button"
                      onClick={() => approveMutation.mutate(item.requestId)}
                      disabled={approveMutation.isPending}
                      className="rounded bg-emerald-700 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
                    >
                      Approve
                    </button>
                  )}
                </div>
              </div>
              {item.complianceSummary && (
                <p className="mt-2 text-xs text-slate-400">{item.complianceSummary}</p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
